import querystring from 'querystring'
import {
  CreatePersonaResponse,
  GetPersonaCountResponse,
  PersonaForm,
  PersonaRequest,
  ServerPersona,
  ServerPersonaData,
} from 'src/api/types/persona'
import { Persona } from 'src/models/Persona'
import { PromisedType } from 'src/utils/types'
import useSWR, { SWRConfiguration } from 'swr'
import { filterUndefined } from '../utils/object'
import { ApiClient, fetcher } from './client'
import {
  GET_PERSONAS,
  GET_PERSONA_SEARCH_COMPANY_COUNT,
  POST_PERSONAS,
  getPersona,
  putPersona,
} from './endpoints'
import { ApiResponse } from './types'
import { CurrentCompany } from './types/currentCompany'

const personaDataFetcher = async (
  url: string,
): Promise<{ personas: Persona[] }> => {
  const res: ServerPersonaData = await fetcher(url)
  return {
    personas: res.map(toPersona),
  }
}

const personaFetcher = async (url: string): Promise<Persona | undefined> => {
  const res: ServerPersona = await fetcher(url)
  return res ? toPersona(res) : undefined
}

const toPersona = (data: ServerPersona): Persona => ({
  id: data.id,
  personaAdminAgeIds: data.personaAdminAgeIds,
  personaBusinessObjectIds: data.personaBusinessObjectIds,
  personaEmployeeNumberIds: data.personaEmployeeNumberIds,
  personaFoundedAtIds: data.personaFoundedAtIds,
  personaPrefectureIds: data.personaPrefectureIds,
  personaTypeOfBusinessIds: data.personaTypeOfBusinessIds,
  personaCompanyBuyNeedOptionIds: data.personaCompanyBuyNeedOptionIds,
  personaSolutionIds: [],
  personaCapitalIds: data.personaCapitalIds,
  personaPositionIds: data.personaPositionIds,
  personaRoleForAnalysisIds: [],
  refuseMessage: data.refuseMessage,
  noMessage: data.noMessage,
  isNew: data.isNew,
  isListedCompany: data.isListedCompany,
  freeWord: data.freeWord || '',
  minCreatedDate: null,
  maxCreatedDate: null,
  minLastSignInAt: null,
  maxLastSignInAt: null,
  personaAdminAges: data.personaAdminAges,
  personaCompanyBuyNeedOptions: data.personaCompanyBuyNeedOptions,
  personaSolutions: [],
  personaBusinessObjects: data.personaBusinessObjects,
  personaEmployeeNumbers: data.personaEmployeeNumbers,
  personaFoundedAts: data.personaFoundedAts,
  personaPrefectures: data.personaPrefectures,
  personaTypeOfBusinesses: data.personaTypeOfBusinesses,
  personaCapitals: data.personaCapitals,
  personaPositions: data.personaPositions,
  personaRoleForAnalysis: [],
})

export const usePersonaList = (config?: Partial<SWRConfiguration>) =>
  useSWR<PromisedType<typeof personaDataFetcher>>(
    GET_PERSONAS,
    personaDataFetcher,
    config,
  )

export const createPersona = async (
  personaForm: PersonaForm,
): Promise<ApiResponse<CreatePersonaResponse>> => {
  const body = toPersonaRequest(personaForm)
  return await ApiClient.post(POST_PERSONAS, body)
}

export const usePersona = (id: number, config?: Partial<SWRConfiguration>) =>
  useSWR<Persona | undefined>(getPersona(id), personaFetcher, config)

export const updatePersona = async (
  id: number,
  personaForm: PersonaForm,
): Promise<
  ApiResponse<{ success: boolean; currentCompany: CurrentCompany }>
> => {
  const body = toPersonaRequest(personaForm)
  return await ApiClient.put(putPersona(id), body)
}

// search persona
export const getPersonaCompanyCount = async (
  params: PersonaForm,
): Promise<ApiResponse<GetPersonaCountResponse>> => {
  return await ApiClient.get(
    `${GET_PERSONA_SEARCH_COMPANY_COUNT}?${toPersonaSearchQuery(params)}`,
  )
}

const toPersonaRequest = (personaForm: PersonaForm): PersonaRequest => ({
  persona: {
    type_of_business_ids: personaForm.typeOfBusinesss.map(({ value }) => value),
    employee_number_ids: personaForm.employeeNumbers.map(({ value }) => value),
    company_buy_need_option_ids: personaForm.companyBuyNeedOptions.map(
      ({ value }) => value,
    ),
    prefecture_ids: personaForm.prefectures.map(({ value }) => value),
    position_ids: personaForm.positions.map(({ value }) => value),
    capital_ids: personaForm.capitals.map(({ value }) => value),
    is_new: personaForm.isNew === undefined ? false : personaForm.isNew,
    no_message:
      personaForm.noMessage === undefined ? false : personaForm.noMessage,
    refuse_message:
      personaForm.refuseMessage === undefined
        ? false
        : personaForm.refuseMessage,
    is_listed_company: personaForm.isListedCompany,
    business_object_ids: personaForm.businessObjects.map(({ value }) => value),
    founded_at_ids: personaForm.foundedAts.map(({ value }) => value),
    admin_age_ids: personaForm.adminAges.map(({ value }) => value),
  },
})

export const toPersonaSearchQuery = (personaForm: PersonaForm) => {
  const query = querystring
    .stringify(
      filterUndefined({
        type_of_business_id:
          personaForm.typeOfBusinesss.map(({ value }) => value).join('|') ||
          undefined,
        employee_se:
          personaForm.employeeNumbers.map(({ value }) => value).join('|') ||
          undefined,
        needs:
          personaForm.companyBuyNeedOptions
            .map(({ value }) => value)
            .join('|') || undefined,
        prefecture_id_se:
          personaForm.prefectures.map(({ value }) => value).join('|') ||
          undefined,
        position:
          personaForm.positions.map(({ value }) => value).join('|') ||
          undefined,
        capital:
          personaForm.capitals.map(({ value }) => value).join('|') || undefined,
        is_listed_company: personaForm.isListedCompany ? '1' : undefined,
        founded_at_se:
          personaForm.foundedAts.map(({ value }) => value).join('|') ||
          undefined,
        business_object_se:
          personaForm.businessObjects.map(({ value }) => value).join('|') ||
          undefined,
        admin_age_se:
          personaForm.adminAges.map(({ value }) => value).join('|') ||
          undefined,
        is_new: personaForm.isNew ? '1' : undefined,
        no_message: personaForm.noMessage ? '1' : undefined,
        refuse_message: personaForm.refuseMessage ? '1' : undefined,
      }),
    )
    .replaceAll('%7C', '|')
  return query ? `&${query}` : ''
}
